import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '@chakra-ui/react';
import env from 'config/env';
import { useEncryption } from './EncryptionContext';

export interface Tag {
  id: string;
  encrypted_name: string;
  decrypted_name?: string;
  color: string;
}

interface TagContextType {
  tags: Tag[];
  loading: boolean;
  createTag: (name: string, color: string) => Promise<Tag>;
  updateTag: (id: string, name: string, color: string) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
  refreshTags: () => Promise<void>;
}

const TagContext = createContext<TagContextType | undefined>(undefined);

export function TagProvider({ children }: { children: React.ReactNode }) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const { encrypt, decrypt } = useEncryption();

  const refreshTags = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${env.API_URL}/tags`);
      const encryptedTags = response.data;

      // Decrypt tag names
      const decryptedTags = await Promise.all(
        encryptedTags.map(async (tag: Tag) => {
          try {
            const decrypted_name = await decrypt(tag.encrypted_name);
            return { ...tag, decrypted_name };
          } catch (error) {
            console.error('Error decrypting tag name:', error);
            return { ...tag, decrypted_name: 'Error decrypting tag name' };
          }
        })
      );

      setTags(decryptedTags);
    } catch (error) {
      toast({
        title: 'Error fetching tags',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast, decrypt]);

  const createTag = useCallback(async (name: string, color: string): Promise<Tag> => {
    try {
      const encrypted_name = await encrypt(name);
      const response = await axios.post<Tag>(`${env.API_URL}/tags`, { encrypted_name, color });
      const newTag = { ...response.data, decrypted_name: name };
      setTags(currentTags => [...currentTags, newTag]);
      return newTag;
    } catch (error) {
      toast({
        title: 'Error creating tag',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  }, [toast, encrypt]);

  const updateTag = useCallback(async (id: string, name: string, color: string): Promise<void> => {
    try {
      const encrypted_name = await encrypt(name);
      const response = await axios.put<Tag>(`${env.API_URL}/tags/${id}`, { encrypted_name, color });
      setTags(currentTags => 
        currentTags.map(tag => tag.id === id ? { ...response.data, decrypted_name: name } : tag)
      );
    } catch (error) {
      toast({
        title: 'Error updating tag',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  }, [toast, encrypt]);

  const deleteTag = useCallback(async (id: string): Promise<void> => {
    try {
      await axios.delete(`${env.API_URL}/tags/${id}`);
      setTags(currentTags => currentTags.filter(tag => tag.id !== id));
    } catch (error) {
      toast({
        title: 'Error deleting tag',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  }, [toast]);

  // Initial load of tags
  React.useEffect(() => {
    refreshTags();
  }, [refreshTags]);

  return (
    <TagContext.Provider
      value={{
        tags,
        loading,
        createTag,
        updateTag,
        deleteTag,
        refreshTags,
      }}
    >
      {children}
    </TagContext.Provider>
  );
}

export function useTags() {
  const context = useContext(TagContext);
  if (context === undefined) {
    throw new Error('useTags must be used within a TagProvider');
  }
  return context;
}

export default TagContext; 